import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Shield, UserCog, Settings, UserPlus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { menuGroups } from "@/components/Sidebar";

// Lista dinâmica de todas as páginas do sistema — gerada a partir do Sidebar
const ALL_PAGES = menuGroups.flatMap((g) =>
  g.items.map((item) => ({ path: item.path, label: item.label, group: g.title }))
);

const GROUPS = Array.from(new Set(ALL_PAGES.map((p) => p.group)));
const PAGE_LABELS = new Map(ALL_PAGES.map((page) => [page.path, page.label]));

function parseNames(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === "string");
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function formatAccess(op: any): string {
  const somenteVinculado = op.somenteVinculado ? "Sim" : "Não";
  const emitente = op.emitenteNome || "Nenhum";
  const subEmitentes = parseNames(op.subEmitenteNomes);
  return `Somente vinculado: ${somenteVinculado} · Emitente: ${emitente} · Subemitentes: ${subEmitentes.length ? subEmitentes.join(" · ") : "Nenhum"}`;
}

function formatPages(op: any): string {
  const pages = parseNames(op.paginasConfiguradas)
    .map((page) => PAGE_LABELS.get(page) || "Página não identificada");
  return pages.length ? pages.join(" · ") : "Nenhuma página configurada";
}

export default function Users() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();

  // Modal de role
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [selectedEmitenteId, setSelectedEmitenteId] = useState<number | null>(null);
  const [selectedVariacaoMaxVenda, setSelectedVariacaoMaxVenda] = useState<string>("");
  const [selectedSomenteVinculado, setSelectedSomenteVinculado] = useState<boolean>(false);
  const [selectedSubEmitentes, setSelectedSubEmitentes] = useState<number[]>([]);

  const [searchTerm, setSearchTerm] = useState("");

  // Modal de criar usuário
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createPassword, setCreatePassword] = useState("");
  const [createRole, setCreateRole] = useState("");

  // Modal de páginas
  const [showPagesModal, setShowPagesModal] = useState(false);
  const [pagesUserId, setPagesUserId] = useState<number | null>(null);
  const [pagesUserName, setPagesUserName] = useState<string>("");
  const [pagesUserRole, setPagesUserRole] = useState<string>("");
  const [selectedPages, setSelectedPages] = useState<Set<string>>(new Set());
  const [pagesSearch, setPagesSearch] = useState("");

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (user && user.role !== "lider" && user.role !== "admin") {
      setLocation("/");
    }
  }, [user, setLocation]);

  const { data: operators = [], refetch } = trpc.users.listOperators.useQuery(
    undefined,
    { enabled: !!user && (user.role === "lider" || user.role === "admin") }
  );

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();

  const createUserMutation = trpc.users.create.useMutation({
    onSuccess: () => {
      toast.success("Usuário criado com sucesso");
      setShowCreateModal(false);
      setCreateName("");
      setCreateEmail("");
      setCreatePassword("");
      setCreateRole("");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateRoleMutation = trpc.users.updateRole.useMutation({
    onSuccess: () => {
      toast.success("Permissão atualizada com sucesso");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Query de permissões de páginas do usuário selecionado
  const { data: existingPages, refetch: refetchPages } = trpc.users.getPagePermissions.useQuery(
    { userId: pagesUserId! },
    { enabled: !!pagesUserId && showPagesModal }
  );

  // Quando as páginas existentes carregam, preencher o Set
  useEffect(() => {
    if (existingPages) {
      setSelectedPages(new Set(existingPages));
    } else if (!showPagesModal) {
      setSelectedPages(new Set());
    }
  }, [existingPages, showPagesModal]);

  const setPagesMutation = trpc.users.setPagePermissions.useMutation({
    onSuccess: () => {
      toast.success("Permissões de páginas salvas com sucesso");
      setShowPagesModal(false);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const openRoleModal = (userId: number) => {
    if (userId === user?.id) {
      toast.error("Você não pode alterar sua própria permissão");
      return;
    }
    setSelectedUserId(userId);
    setSelectedRole("");
    setSelectedEmitenteId(null);
    setSelectedVariacaoMaxVenda("");
    setSelectedSomenteVinculado(false);
    setSelectedSubEmitentes([]);
    setShowRoleModal(true);
  };

  const handleRoleChange = () => {
    if (!selectedUserId || !selectedRole) {
      toast.error("Selecione uma permissão");
      return;
    }

    updateRoleMutation.mutate({ userId: selectedUserId, role: selectedRole as any, emitenteId: selectedEmitenteId || undefined, variacaoMaxVenda: selectedVariacaoMaxVenda ? Number(selectedVariacaoMaxVenda) : null, somenteVinculado: selectedSomenteVinculado, subEmitenteIds: selectedSubEmitentes.length > 0 ? selectedSubEmitentes : undefined });
    setShowRoleModal(false);
  };

  const openPagesModal = (op: any) => {
    setPagesUserId(op.id);
    setPagesUserName(op.name || op.email || `Usuário #${op.id}`);
    setPagesUserRole(op.role);
    setPagesSearch("");
    setShowPagesModal(true);
  };

  const togglePage = (path: string) => {
    setSelectedPages((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const toggleGroup = (group: string) => {
    const groupPages = ALL_PAGES.filter((p) => p.group === group).map((p) => p.path);
    const allSelected = groupPages.every((p) => selectedPages.has(p));
    setSelectedPages((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        groupPages.forEach((p) => next.delete(p));
      } else {
        groupPages.forEach((p) => next.add(p));
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedPages(new Set(ALL_PAGES.map((p) => p.path)));
  };

  const clearAll = () => {
    setSelectedPages(new Set());
  };

  const savePagesPermissions = () => {
    if (!pagesUserId) return;
    setPagesMutation.mutate({ userId: pagesUserId, pages: Array.from(selectedPages) });
  };

  const filteredPages = pagesSearch.trim()
    ? ALL_PAGES.filter((p) =>
        p.label.toLowerCase().includes(pagesSearch.toLowerCase()) ||
        p.path.toLowerCase().includes(pagesSearch.toLowerCase())
      )
    : ALL_PAGES;

  const filteredGroups = Array.from(new Set(filteredPages.map((p) => p.group)));

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== "lider" && user.role !== "admin")) {
    return null;
  }

  const getRoleBadge = (role: string) => {
    switch (role) {
      case "admin": return "bg-red-100 text-red-800 border-red-200";
      case "lider": return "bg-blue-100 text-blue-800 border-blue-200";
      case "operador": return "bg-gray-100 text-gray-800 border-gray-200";
      case "cliente": return "bg-purple-100 text-purple-800 border-purple-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case "admin": return "Administrador";
      case "lider": return "Líder";
      case "operador": return "Operador";
      case "cliente": return "Cliente";
      case "user": return "Usuário";
      default: return role;
    }
  };

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="sticky top-0 z-10 bg-card border-b shadow-sm">
          <div className="container py-4">
            <h1 className="text-2xl font-bold text-foreground">Gerenciar Usuários</h1>
          </div>
        </div>
        <div className="container py-6">
          <div className="bg-card rounded-lg border shadow-sm">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <UserCog className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-semibold">Usuários e Permissões</h2>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar usuário..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-56"
                    />
                  </div>
                  {user.role === "admin" && (
                    <Button size="sm" onClick={() => setShowCreateModal(true)}>
                      <UserPlus className="h-4 w-4 mr-1" />
                      Novo Usuário
                    </Button>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Nome</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Email</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Permissão Atual</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Acesso</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Páginas</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(() => {
                      const s = searchTerm.toLowerCase();
                      const filteredOperators = operators.filter((op: any) =>
                        (op.name && op.name.toLowerCase().includes(s)) ||
                        (op.email && op.email.toLowerCase().includes(s)) ||
                        op.role.toLowerCase().includes(s)
                      );
                      return filteredOperators.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-muted-foreground">
                          Nenhum usuário encontrado
                        </td>
                      </tr>
                    ) : (
                      filteredOperators.map((op: any) => (
                        <tr key={op.id} className="hover:bg-muted/30">
                          <td className="py-3 px-4 text-sm font-mono text-muted-foreground">#{op.id}</td>
                          <td className="py-3 px-4 text-sm font-medium">{op.name || "-"}</td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">{op.email || "-"}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadge(op.role)}`}>
                              <Shield className="h-3 w-3 mr-1" />
                              {getRoleLabel(op.role)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground min-w-80">
                            {formatAccess(op)}
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground min-w-80">
                            {formatPages(op)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-2">
                              {op.id !== user.id && user.role === "admin" && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openRoleModal(op.id)}
                                    disabled={updateRoleMutation.isPending}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  >
                                    Alterar Permissão
                                  </Button>
                                  {/* Botão Configurar Páginas — apenas para lider, operador e cliente */}
                                  {(op.role === "lider" || op.role === "operador" || op.role === "cliente") && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openPagesModal(op)}
                                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    >
                                      <Settings className="h-3 w-3 mr-1" />
                                      Configurar Páginas
                                    </Button>
                                  )}
                                </>
                              )}
                              {op.id === user.id && (
                                <span className="text-xs text-muted-foreground italic">Você (não pode alterar)</span>
                              )}
                              {op.id !== user.id && user.role !== "admin" && (
                                <span className="text-xs text-muted-foreground italic">Apenas admin pode alterar</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    );
                    })()}
                  </tbody>
                </table>
              </div>
              <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                <h3 className="text-sm font-semibold mb-2">Informações sobre Permissões</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Administrador:</strong> Acesso total ao sistema, sem filtro de emitente</li>
                  <li>• <strong>Líder / Operador:</strong> Acesso às páginas configuradas, sem filtro de emitente</li>
                  <li>• <strong>Cliente:</strong> Acesso às páginas configuradas, com filtro pelo emitente vinculado</li>
                  <li>• <strong>Usuário:</strong> Sem acesso — vê apenas a tela de solicitação de acesso</li>
                  <li>• Você não pode alterar sua própria permissão</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Alteração de Role */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Novo Usuário</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Nome</label>
                <Input
                  className="w-full mt-1"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder=""
                />
              </div>
              <div>
                <label className="text-sm font-medium">E-mail</label>
                <Input
                  type="email"
                  className="w-full mt-1"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  placeholder=""
                />
              </div>
              <div>
                <label className="text-sm font-medium">Senha</label>
                <Input
                  type="password"
                  className="w-full mt-1"
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  placeholder=""
                />
              </div>
              <div>
                <label className="text-sm font-medium">Permissão</label>
                <select
                  value={createRole}
                  onChange={(e) => setCreateRole(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                >
                  <option value="">Selecione...</option>
                  <option value="user">Usuário (sem acesso)</option>
                  <option value="operador">Operador</option>
                  <option value="lider">Líder</option>
                  <option value="admin">Administrador</option>
                  <option value="cliente">Cliente</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
              <Button
                onClick={() => {
                  if (!createName.trim() || !createEmail.trim() || createPassword.length < 6 || !createRole) {
                    toast.error("Preencha todos os campos. Senha mínima: 6 caracteres.");
                    return;
                  }
                  createUserMutation.mutate({ name: createName.trim(), email: createEmail.trim(), password: createPassword, role: createRole });
                }}
                disabled={createUserMutation.isPending}
              >
                {createUserMutation.isPending ? "Criando..." : "Criar Usuário"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showRoleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Alterar Permissão</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Nova Permissão</label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                >
                  <option value="">Selecione...</option>
                  <option value="user">Usuário (sem acesso)</option>
                  <option value="operador">Operador</option>
                  <option value="lider">Líder</option>
                  <option value="admin">Administrador</option>
                  <option value="cliente">Cliente</option>
                </select>
              </div>
              {selectedRole === "cliente" && (
                <div>
                  <label className="text-sm font-medium">Emitente Vinculado *</label>
                  <select
                    value={selectedEmitenteId || ""}
                    onChange={(e) => setSelectedEmitenteId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full mt-1 px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="">Selecione um emitente...</option>
                    {emitentes.map((em: any) => (
                      <option key={em.id} value={em.id}>{em.razaoSocial}</option>
                    ))}
                  </select>
                </div>
              )}
              {selectedRole === "cliente" && (
                <div>
                  <label className="text-sm font-medium">Variação Máx. Venda (%)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={selectedVariacaoMaxVenda}
                    onChange={(e) => setSelectedVariacaoMaxVenda(e.target.value)}
                    placeholder="Ex: 10 (permite até 10% abaixo do valor de venda)"
                    className="mt-1"
                  />
                </div>
              )}
              {selectedRole === "cliente" && (
                <div className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    id="somenteVinculado"
                    checked={selectedSomenteVinculado}
                    onChange={(e) => setSelectedSomenteVinculado(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <label htmlFor="somenteVinculado" className="text-sm font-medium">Visualizar somente vinculado</label>
                </div>
              )}
              {selectedRole === "cliente" && (
                <div className="mt-3">
                  <label className="text-sm font-medium">Sub-emitentes</label>
                  <div className="mt-1 max-h-40 overflow-y-auto border rounded p-2 space-y-1">
                    {emitentes.filter((e: any) => e.id !== selectedEmitenteId).map((e: any) => (
                      <div key={e.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`sub-emitente-${e.id}`}
                          checked={selectedSubEmitentes.includes(e.id)}
                          onChange={(ev) => {
                            if (ev.target.checked) {
                              setSelectedSubEmitentes([...selectedSubEmitentes, e.id]);
                            } else {
                              setSelectedSubEmitentes(selectedSubEmitentes.filter((id) => id !== e.id));
                            }
                          }}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <label htmlFor={`sub-emitente-${e.id}`} className="text-sm">{e.razaoSocial || e.nomeFantasia || `Emitente ${e.id}`}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowRoleModal(false)}>Cancelar</Button>
                <Button onClick={handleRoleChange} disabled={updateRoleMutation.isPending}>Confirmar</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Configuração de Páginas */}
      {showPagesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg w-full mx-4" style={{ maxWidth: "700px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            {/* Header */}
            <div className="p-5 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Configurar Páginas</h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {pagesUserName} — <span className="font-medium">{getRoleLabel(pagesUserRole)}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">{selectedPages.size}</span> de {ALL_PAGES.length} páginas selecionadas
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Input
                  placeholder="Buscar página..."
                  value={pagesSearch}
                  onChange={(e) => setPagesSearch(e.target.value)}
                  className="flex-1"
                />
                <Button size="sm" variant="outline" onClick={selectAll}>Todas</Button>
                <Button size="sm" variant="outline" onClick={clearAll}>Nenhuma</Button>
              </div>
            </div>

            {/* Lista de páginas agrupadas */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {filteredGroups.map((group) => {
                const groupPages = filteredPages.filter((p) => p.group === group);
                const allGroupSelected = groupPages.every((p) => selectedPages.has(p.path));
                const someGroupSelected = groupPages.some((p) => selectedPages.has(p.path));
                return (
                  <div key={group} className="border rounded-lg overflow-hidden">
                    {/* Cabeçalho do grupo */}
                    <div
                      className="flex items-center gap-3 px-4 py-2.5 bg-muted/40 cursor-pointer hover:bg-muted/60 select-none"
                      onClick={() => toggleGroup(group)}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                        allGroupSelected
                          ? "bg-primary border-primary"
                          : someGroupSelected
                          ? "bg-primary/40 border-primary"
                          : "border-gray-300"
                      }`}>
                        {(allGroupSelected || someGroupSelected) && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d={allGroupSelected ? "M5 13l4 4L19 7" : "M5 12h14"} />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-semibold">{group}</span>
                      <span className="text-xs text-muted-foreground ml-auto">
                        {groupPages.filter((p) => selectedPages.has(p.path)).length}/{groupPages.length}
                      </span>
                    </div>
                    {/* Itens do grupo */}
                    <div className="divide-y">
                      {groupPages.map((page) => (
                        <label
                          key={page.path}
                          className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-muted/20 select-none"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPages.has(page.path)}
                            onChange={() => togglePage(page.path)}
                            className="w-4 h-4 rounded accent-primary"
                          />
                          <span className="text-sm flex-1">{page.label}</span>
                          <span className="text-xs text-muted-foreground font-mono">{page.path}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
              {filteredGroups.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Nenhuma página encontrada para "{pagesSearch}"
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t flex justify-end gap-2 flex-shrink-0">
              <Button variant="outline" onClick={() => setShowPagesModal(false)}>Cancelar</Button>
              <Button onClick={savePagesPermissions} disabled={setPagesMutation.isPending}>
                {setPagesMutation.isPending ? "Salvando..." : "Salvar Permissões"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
